import Color from 'color';

import type { HexAlpha } from '../types/common';
import { silenceError } from './common';
import { isDefined, isEmpty } from './guards';

/**
 * Converts OKLCH color to HexAlpha format without using Color library
 * @param {string} oklchColor - OKLCH color string (e.g. "oklch(0.553 0.013 58.071)" or "oklch(0.553 0.013 58.071 / 0.5)")
 * @returns {HexAlpha} Color in HexAlpha format
 */
export const oklchToHexAlpha = (oklchColor: string): HexAlpha => {
	try {
		/** match both 3 and 4 component OKLCH colors with optional alpha */
		const match = oklchColor.match(/oklch\(\s*([-+]?[0-9.]+%?)\s+([-+]?[0-9.]+%?)\s+([-+]?[0-9.]+%?)(?:\s*\/\s*([-+]?[0-9.]+%?))?\s*\)/i);
		if (!isDefined(match)) {
			throw new Error('Invalid OKLCH format');
		}

		/** lightness [0,1] - handle percentage notation */
		const lightnessStr = match[1]!;
		let lightness: number;
		if (lightnessStr.endsWith('%')) {
			/** convert percentage to decimal */
			lightness = parseFloat(lightnessStr) / 100;
		} else {
			lightness = parseFloat(lightnessStr);
		}

		/** chroma [0,0.4] - handle percentage notation */
		const chromaStr = match[2]!;
		let chroma: number;
		if (chromaStr.endsWith('%')) {
			/** convert percentage to decimal */
			chroma = parseFloat(chromaStr) / 100;
		} else {
			chroma = parseFloat(chromaStr);
		}

		/** hue [0,360] - handle percentage notation */
		const hueStr = match[3]!;
		let hue: number;
		if (hueStr.endsWith('%')) {
			/** convert percentage to 360 degrees */
			hue = parseFloat(hueStr) * 3.6;
		} else {
			hue = parseFloat(hueStr);
		}

		/** alpha [0,1] - handle percentage notation */
		let alpha = 1.0;
		if (isDefined(match[4])) {
			const alphaStr = match[4]!;
			if (alphaStr.endsWith('%')) {
				/** convert percentage to decimal */
				alpha = parseFloat(alphaStr) / 100;
			} else {
				alpha = parseFloat(alphaStr);
			}
		}

		/** validate input values */
		if (Number.isNaN(lightness) || Number.isNaN(chroma) || Number.isNaN(hue) || Number.isNaN(alpha)) {
			throw new Error('Invalid OKLCH values');
		}

		/** clamp values to valid ranges */
		const clampedL = Math.max(0, Math.min(1, lightness));
		/** no upper limit, will be naturally constrained by conversion */
		const clampedC = Math.max(0, chroma);
		/** normalize hue to [0,360) */
		const normalizedH = ((hue % 360) + 360) % 360;
		/** clamp alpha to [0,1] */
		const clampedAlpha = Math.max(0, Math.min(1, alpha));

		/** convert OKLCH to OKLAB */
		const hRad = normalizedH * (Math.PI / 180);
		const a = clampedC * Math.cos(hRad);
		const b = clampedC * Math.sin(hRad);

		/** convert OKLAB to linear sRGB using improved transformation matrix */
		const l_ = clampedL + 0.3963377774 * a + 0.2158037573 * b;
		const m_ = clampedL - 0.1055613458 * a - 0.0638541728 * b;
		const s_ = clampedL - 0.0894841775 * a - 1.291485548 * b;

		/** non-linear transformation */
		const l = l_ * l_ * l_;
		const m = m_ * m_ * m_;
		const s = s_ * s_ * s_;

		/** convert LMS to linear RGB */
		let red = +4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s;
		let green = -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s;
		let blue = -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s;

		/** clamp and apply gamma correction (linear to sRGB) */
		const toGamma = (u: number): number => {
			u = Math.max(0, Math.min(1, u));
			return u <= 0.0031308 ? 12.92 * u : 1.055 * Math.pow(u, 1 / 2.4) - 0.055;
		};

		red = toGamma(red);
		green = toGamma(green);
		blue = toGamma(blue);

		/** convert to 8-bit values and then to hex */
		const toHex = (v: number): string => {
			const int = Math.round(v * 255);
			const clampedInt = Math.max(0, Math.min(255, int));
			const hex = clampedInt.toString(16).toUpperCase();
			return hex.length === 1 ? '0' + hex : hex;
		};

		/** convert alpha to hex */
		const alphaHex = toHex(clampedAlpha);

		return `#${toHex(red)}${toHex(green)}${toHex(blue)}${alphaHex}` as HexAlpha;
	} catch (e) {
		/** gracefully handle invalid input */
		console.warn('[oklchToHexAlpha] invalid color input', oklchColor, e);
		return '#000000FF';
	}
};

/**
 * Returns correct Hex-alpha color string
 * @param {string} color
 */
export const hexAlpha = (color: string): HexAlpha => {
	/** handle empty input */
	if (isEmpty(color)) {
		return '#000000FF';
	}

	const colorWithoutHex = color?.replace('#', '');

	/** handle standard hex colors */
	if (/^[0-9A-Fa-f]{6}$/i.test(colorWithoutHex)) return `#${colorWithoutHex.toUpperCase()}FF` as HexAlpha;
	if (/^[0-9A-Fa-f]{8}$/i.test(colorWithoutHex)) return `#${colorWithoutHex.toUpperCase()}` as HexAlpha;

	/** handle oklch colors */
	if (color.toLowerCase().startsWith('oklch')) {
		return oklchToHexAlpha(color);
	}

	/** try using Color library for other color formats */
	try {
		return new Color(color).hexa() as HexAlpha;
	} catch (e) {
		console.warn('[hexAlpha] invalid color input', color);
		return '#000000FF';
	}
};

/**
 * Converts color to Color via `color` library, chains functions in callback and returns back hex-alpha string
 * @param {HexAlpha} color
 * @param {(clr: Color) => Color} cb
 */
export const modifyColor = (color: HexAlpha, cb: (clr: Color) => Color): HexAlpha => cb(Color(color)).hexa() as HexAlpha;

/**
 * Returns value from Color callback
 * @param {HexAlpha} color
 * @param {(clr: Color) => Ret} cb
 */
export const getColor = <Ret extends any>(color: HexAlpha, cb: (clr: Color) => Ret) => cb(Color(color));

/**
 * Returns a Color object from raw input
 * @param {Color | string | ArrayLike<number> | number | {[p: string]: any}} input
 */
export const getHexColorFromInput = (
	input:
		| Color
		| string
		| ArrayLike<number>
		| number
		| {
				[key: string]: any;
		  },
) => Color(input);

/**
 * Creates a Color object from a color input
 * @param {HexAlpha} color
 */
export const createColor = (color: string) => Color(color);

/**
 * Returns a contrast text color for a given color
 * @param {string} color
 */
export const getContrastColorTo = (color: string) =>
	silenceError(
		() => modifyColor(hexAlpha(createColor(color).hexa()), (c) => (c.isLight() ? createColor('#000000') : createColor('#FFFFFF'))),
		'#FFFFFF',
	);

/**
 * Returns CSS color styles for a given color
 * @param color A color string to get styles for
 * @returns HEX color string for background and contract HEX color for text
 */
export const getColorStyles = (color: string) => {
	const hex = hexAlpha(color);
	const contrast = getContrastColorTo(hex);
	return {
		color: contrast,
		backgroundColor: hex,
	};
};
